import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { ShieldCheck, CheckCircle2, MinusCircle, XCircle, Loader2, Compass, RefreshCw, Bell, ArrowUpRight, ArrowDownRight, Wrench } from 'lucide-react';

// AI Coverage dashboard (Trusted-AI Phase E). Says up front, per model and per
// question pattern (descriptive → prescriptive), what the assistant can fully
// answer, partially answer, or is blocked on — and the missing dependency.
const STATUS = {
  supported: { icon: CheckCircle2, label: 'Supported', cls: 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
  partial: { icon: MinusCircle, label: 'Partial', cls: 'text-amber-600 dark:text-amber-400 bg-amber-500/10 border-amber-500/30' },
  blocked: { icon: XCircle, label: 'Blocked', cls: 'text-rose-600 dark:text-rose-400 bg-rose-500/10 border-rose-500/30' },
};

function StatusPill({ status }) {
  const s = STATUS[status] || STATUS.blocked;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] border ${s.cls}`} data-testid={`coverage-status-${status}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

export default function AiCoveragePage() {
  const navigate = useNavigate();
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [changes, setChanges] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [cadence, setCadence] = useState('daily');
  const [savingCadence, setSavingCadence] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.exploreCoverage();
      setModels(res.models || []);
    } catch (e) {
      toast.error(e.message || 'Could not load coverage');
    } finally { setLoading(false); }
  };

  const loadCadence = async () => {
    try {
      const s = await api.getCoverageDigestSettings();
      setCadence(s.cadence || 'daily');
    } catch (e) { /* steward-only read; non-stewards keep the default */ }
  };

  const changeCadence = async (next) => {
    const prev = cadence;
    setCadence(next); setSavingCadence(true);
    try {
      await api.setCoverageDigestCadence(next);
      toast.success(next === 'off' ? 'Coverage digest turned off' : `Coverage digest set to ${next}`);
    } catch (e) {
      setCadence(prev);
      toast.error(e.message || 'Could not update digest cadence');
    } finally { setSavingCadence(false); }
  };

  // Detect capabilities newly gained/lost since the last snapshot. Changes are
  // rolled into the steward digest. Silent on the very first (baseline) scan.
  const scan = async (announce = false) => {
    if (announce) setScanning(true);
    try {
      const res = await api.exploreCoverageScan();
      setChanges(res.changes || []);
      if (announce) {
        const n = (res.changes || []).reduce((a, c) => a + (c.gained?.length || 0) + (c.lost?.length || 0), 0);
        toast[n ? 'success' : 'info'](n ? `${n} capability change${n === 1 ? '' : 's'} detected` : 'No capability changes since the last check');
      }
    } catch (e) {
      if (announce) toast.error(e.message || 'Could not check for changes');
    } finally { if (announce) setScanning(false); }
  };

  // Demo helper: re-arm the seeded "capability gained" moment, then re-scan so
  // the gain shows immediately — no full demo-environment reset needed.
  const [rearming, setRearming] = useState(false);
  const hasReadinessDemo = models.some((m) => m.model_id === 'dseed_readiness_upgrade_model');
  const rearmDemo = async () => {
    setRearming(true);
    try {
      const r = await api.exploreCoverageRearmDemo();
      if (r.rearmed) { await scan(true); }
      else toast.info('Demo model not present in this workspace');
    } catch (e) {
      toast.error(e.message || 'Could not re-arm the demo');
    } finally { setRearming(false); }
  };

  useEffect(() => { load(); scan(false); loadCadence(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="ai-coverage-page">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Compass className="w-6 h-6 text-primary" /> AI Readiness</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            What the assistant can answer on each model — up front and honestly. Every assessment is deterministic:
            it probes the model's own governed metadata (component measures, a comparable time series, an approved
            predictive model, a benchmark source). Nothing here is guessed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-border/50" title="How often the capability-change digest is emailed to stewards">
            <Bell className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-[11px] text-muted-foreground">Digest</span>
            <select value={cadence} onChange={(e) => changeCadence(e.target.value)} disabled={savingCadence}
              aria-label="Coverage digest cadence"
              className="h-6 text-xs bg-transparent border-0 focus:outline-none disabled:opacity-50" data-testid="coverage-digest-cadence">
              <option value="off">Off</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
            </select>
          </div>
          {hasReadinessDemo && (
            <button onClick={rearmDemo} disabled={rearming || scanning} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-amber-500/40 text-amber-700 dark:text-amber-300 hover:bg-amber-500/10 disabled:opacity-50" data-testid="coverage-rearm-demo-btn" title="Reset the demo model so the next check shows a newly gained capability">
              {rearming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Replay “capability gained”
            </button>
          )}
          <button onClick={() => scan(true)} disabled={scanning} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-50" data-testid="coverage-scan-btn">
            {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />} Check for capability changes
          </button>
          <button onClick={load} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs border border-border/50 hover:bg-muted/40" data-testid="coverage-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </div>
      </div>

      {changes.length > 0 && (
        <div className="mb-4 rounded-xl border border-primary/30 bg-primary/[0.05] p-3" data-testid="coverage-changes">
          <p className="text-xs font-semibold text-primary flex items-center gap-1.5 mb-2">
            <Bell className="w-3.5 h-3.5" /> Coverage changed since the last check — rolled into the daily steward digest.
          </p>
          <div className="space-y-1.5">
            {changes.map((ch) => (
              <div key={ch.model_id} className="text-xs" data-testid={`coverage-change-${ch.model_id}`}>
                <span className="font-medium">{ch.model_name || ch.model_id}:</span>
                <span className="ml-1.5 inline-flex flex-wrap gap-1.5 align-middle">
                  {(ch.gained || []).map((g) => (
                    <span key={`g${g.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30">
                      <ArrowUpRight className="w-3 h-3" /> gained {g.label.split(' — ')[0]} ({g.from}→{g.to})
                    </span>
                  ))}
                  {(ch.lost || []).map((l) => (
                    <span key={`l${l.id}`} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/30">
                      <ArrowDownRight className="w-3 h-3" /> lost {l.label.split(' — ')[0]} ({l.from}→{l.to})
                    </span>
                  ))}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : models.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="coverage-empty">
          <ShieldCheck className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No semantic models found. Connect a source and model a metric to see coverage.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {models.map((m) => (
            <div key={m.model_id} className="rounded-xl border border-border/50 bg-card/40 p-4" data-testid={`coverage-model-${m.model_id}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-semibold">{m.model_name || m.model_id}</h2>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-emerald-600 dark:text-emerald-400">{m.counts?.supported || 0} supported</span>
                  <span className="text-amber-600 dark:text-amber-400">{m.counts?.partial || 0} partial</span>
                  <span className="text-rose-600 dark:text-rose-400">{m.counts?.blocked || 0} blocked</span>
                </div>
              </div>
              <div className="space-y-2">
                {(m.capabilities || []).map((c) => (
                  <div key={c.id} className="flex items-start gap-3 p-2.5 rounded-lg border border-border/30 bg-background/40" data-testid={`coverage-cap-${m.model_id}-${c.id}`}>
                    <div className="pt-0.5"><StatusPill status={c.status} /></div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium">{c.label}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{c.reason}</p>
                      {Array.isArray(c.how_to_fix) && c.how_to_fix.length > 0 && (
                        <div className="mt-2 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] p-2.5" data-testid={`coverage-fix-${m.model_id}-${c.id}`}>
                          <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                            <Wrench className="w-3 h-3" /> How to fix
                          </p>
                          <ol className="mt-1 ml-1 space-y-1 list-decimal list-inside">
                            {c.how_to_fix.map((step, i) => (
                              <li key={i} className="text-[11px] text-muted-foreground leading-snug">{step}</li>
                            ))}
                          </ol>
                          {c.fix?.route && (
                            <button
                              type="button"
                              onClick={() => navigate(c.fix.route)}
                              data-testid={`coverage-fix-btn-${m.model_id}-${c.id}`}
                              className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium border border-amber-500/50 text-amber-700 dark:text-amber-300 hover:bg-amber-500/15"
                            >
                              {c.fix.label || 'Fix this'} <ArrowUpRight className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      )}
                      {!Array.isArray(c.how_to_fix) && c.dependency && (
                        <p className="text-[11px] text-amber-700 dark:text-amber-300 mt-0.5">Needs: {c.dependency}</p>
                      )}
                      <p className="text-[10px] text-muted-foreground/70 italic mt-1">e.g. “{c.example}”</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
